import { Button, Form, Input, Modal, Table, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CopyOutlined, CopyFilled } from "@ant-design/icons";
import {
  partnerTrackingLinksSelector,
  shortLinkSelector,
} from "../../feature/partner-details/partnerDetailsSelector";
import {
  createShortLinkRequest,
  getPartnerTrackingLinksRequest,
} from "../../feature/partner-details/partnerDetailsSlice";
import { partnerInfoSelector } from "../../feature/referral-partners/referralPartnerSelector";
import { appConfig } from "../../utils/appConfig";

const TrackingLinks = ({ id }) => {
  const [selectedTrackingLink, setSelectedTrackingLink] = useState();
  const [displayOnTypeAlias, setDisplayOnTypeAlias] = useState(false);
  const [websiteCopy, setWebsiteCopy] = useState(false);
  const [trackingLinkCopy, setTrackingLinkCopy] = useState(false);
  const [shortLinkCopy, setShortLinkCopy] = useState(false);
  const { listPartnerTrackingLink } = useSelector(({ partnerDetailsState }) =>
    partnerTrackingLinksSelector(partnerDetailsState)
  );
  const { partnerInfo } = useSelector(({ referralPartnersState }) =>
    partnerInfoSelector(referralPartnersState)
  );
  const { isCreateShortLink, isAliasAlreadyCreated } = useSelector(
    ({ partnerDetailsState }) => shortLinkSelector(partnerDetailsState)
  );
  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const columns = useMemo(() => {
    const result = [
      {
        title: "Link Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Link Code",
        dataIndex: "code",
        key: "code",
      },
      {
        title: "Website Address",
        dataIndex: "link",
        key: "link",
        render: (link, item) => (
          <div className="d-flex space-between">
            <a href={link} target="_blank" rel="noreferrer">
              {link}
            </a>

            {websiteCopy[item._id] ? (
              <Tooltip title="Copied!">
                <CopyFilled
                  className="pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(link);
                    setWebsiteCopy({ ...websiteCopy, [item._id]: true });
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Copy">
                <CopyOutlined
                  className="pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(link);
                    setWebsiteCopy({ ...websiteCopy, [item._id]: true });
                  }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Referral Link",
        dataIndex: "link",
        key: "link",
        render: (_, item) =>
          item?.code &&
          partnerInfo?.code &&
          process.env.REACT_APP_REFERRAL && (
            <div className="d-flex space-between">
              <a
                href={`${process.env.REACT_APP_REFERRAL}?lc=${item.code}&pc=${partnerInfo.code}`}
                target="_blank"
                rel="noreferrer"
              >
                {`${process.env.REACT_APP_REFERRAL}?lc=${item.code}&pc=${partnerInfo.code}`}
              </a>

              {trackingLinkCopy[item._id] ? (
                <Tooltip title="Copied!">
                  <CopyFilled
                    className="pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${process.env.REACT_APP_REFERRAL}?lc=${item.code}&pc=${partnerInfo.code}`
                      );
                      setTrackingLinkCopy({
                        ...trackingLinkCopy,
                        [item._id]: true,
                      });
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Copy">
                  <CopyOutlined
                    className="pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${process.env.REACT_APP_REFERRAL}?lc=${item.code}&pc=${partnerInfo.code}`
                      );
                      setTrackingLinkCopy({
                        ...trackingLinkCopy,
                        [item._id]: true,
                      });
                    }}
                  />
                </Tooltip>
              )}
            </div>
          ),
      },
      {
        title: "Referral Link (shortened - Facebook Friendly)",
        dataIndex: "shortLink",
        key: "shortLink",
        render: (shortLink, item) =>
          shortLink?.[0] ? (
            <div className="d-flex space-between">
              <a href={shortLink[0]} target="_blank" rel="noreferrer">
                {shortLink[0]}
              </a>
              {shortLinkCopy[item._id] ? (
                <Tooltip title="Copied!">
                  <CopyFilled
                    className="pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(shortLink[0]);
                      setShortLinkCopy({ ...shortLinkCopy, [item._id]: true });
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Copy">
                  <CopyOutlined
                    className="pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(shortLink[0]);
                      setShortLinkCopy({ ...shortLinkCopy, [item._id]: true });
                    }}
                  />
                </Tooltip>
              )}
            </div>
          ) : (
            <Button
              type="primary"
              onClick={() => {
                setSelectedTrackingLink(item);
                setDisplayOnTypeAlias(true);
              }}
            >
              Click to generate
            </Button>
          ),
      },
    ];
    return result;
    // eslint-disable-next-line
  }, [
    listPartnerTrackingLink,
    selectedTrackingLink,
    isCreateShortLink,
    websiteCopy,
    trackingLinkCopy,
    shortLinkCopy,
  ]);

  const onCreateShortLink = (values) => {
    selectedTrackingLink &&
      dispatch(
        createShortLinkRequest({
          trackingLinkId: selectedTrackingLink._id,
          partnerId: partnerInfo._id,
          alias: values.alias,
        })
      );
  };

  useEffect(() => {
    dispatch(getPartnerTrackingLinksRequest(id));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isAliasAlreadyCreated) {
      form.setFields([
        {
          name: "alias",
          errors: ["Alias has been taken. Please try another one."],
        },
      ]);
    }
    if (!isCreateShortLink && !isAliasAlreadyCreated) {
      form.resetFields();
      setDisplayOnTypeAlias(false);
    }
  }, [isAliasAlreadyCreated, isCreateShortLink, form]);

  return (
    <div className="ledger-container">
      <div className="mt-25">
        <Table
          className="mt-5"
          columns={columns}
          dataSource={listPartnerTrackingLink}
          bordered
          pagination={{
            pageSize: appConfig.PAGE_SIZE,
          }}
          rowKey="_id"
          scroll={{ x: 1100 }}
        />
      </div>
      {displayOnTypeAlias && (
        <Modal
          title="Enter the Alias for Short link"
          visible={true}
          width={"50%"}
          className="modal-w-600"
          onCancel={() => {
            form.resetFields();
            setDisplayOnTypeAlias(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then((values) => {
                onCreateShortLink(values);
              })
              .catch((info) => {
                console.log("Validate Failed:", info);
              });
          }}
          okButtonProps={{ disabled: isCreateShortLink }}
          cancelButtonProps={{ disabled: isCreateShortLink }}
          confirmLoading={isCreateShortLink}
        >
          <Form
            form={form}
            name="form-alias"
            id="form-alias"
            className="row-col"
            layout="vertical"
          >
            <Form.Item
              name="alias"
              label="Alias"
              rules={[
                {
                  required: true,
                  message: "Please input your referral partner code!",
                },
                { min: 5, message: "Code must be minimum 5 characters" },
                {
                  pattern: new RegExp("^[a-zA-Z0-9]{4,}$"),
                  message:
                    "Value is only number & char, no special char, no space",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default TrackingLinks;
